// RealVideosPage.tsx — the Videos page on real posts (2026-09-26), laid out after the
// owner's concept (docs/References/Images/videos_page.webp):
//
//   chips  → All · Following · Trending · Live · Flash · Gaming · News · Education · Music ·
//            Tech · Sports
//   All    → Featured hero (with why) · Trending Now (or Latest) · Flash
//   other  → that chip's grid, "Show more" paging
//   rail   → Up Next · Live Channels (coming soon) · Watch Later
//
// Owner decisions: Live is shown as "coming soon" (no fake channels); Watch Later is the
// built-in Bookmarks folder; Flash = videos of 60 s or less (formerly "Shorts", renamed by
// the owner 2026-09-26), marked by the uploader.
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { media } from '@/theme/media';
import { Button, Panel, SectionLabel } from '@/ui';
import { FilterChips } from '@/components/FilterChips/FilterChips';
import { NetworkLinks } from '@/features/networks/NetworkLinks';
import { useRightRail, RailGroup, RailPanel } from '@/app/shell/RightRail';
import { useIsMuted, useSession } from '@/hooks';
import type { Post } from '@/core/models/post';
import type { VideoFilter, VideosHome } from '@/core/services/videoFeedService';
import { CHIP_IDS, FEATURED_REASON, REAL_CHIPS, type RealChipId } from './chips';
import { FeaturedVideoHero } from '../FeaturedVideoHero';
import { VideoCard } from '../VideoCard';
import { RailVideoItem } from '../RailVideoItem';
import { FeaturedPostMenu } from './FeaturedPostMenu';
import { ShortClipCard } from './ShortClipCard';
import { toCards, toCreator, toVideoItem } from './realItems';
import { useVideoList, useWatchLater } from './useRealVideos';

function chipFilter(chip: RealChipId): VideoFilter | null {
  if (chip === 'all' || chip === 'live') return null;
  if (chip === 'flash') return 'shorts';
  return chip;
}

const Header = styled.div`
  margin-bottom: 14px;
`;

const Title = styled.h1`
  margin: 0 0 4px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Subtitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin: 22px 0 12px;
`;

const SectionHeading = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const ViewAll = styled(Link)`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.accentCyan};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  ${media.mobile`grid-template-columns: 1fr;`}
`;

const ShortsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
  ${media.mobile`grid-template-columns: repeat(2, minmax(0, 1fr));`}
`;

const Note = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const More = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const RailList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RailNote = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

function VideoGrid({ posts }: { posts: Post[] }) {
  const { items, creators } = toCards(posts);
  return (
    <Grid>
      {items.map((v) => (
        <VideoCard key={v.id} video={v} creator={creators[v.authorId]} />
      ))}
    </Grid>
  );
}

function ShortsRow({ posts }: { posts: Post[] }) {
  return (
    <ShortsGrid data-testid="flash-row">
      {posts.map((post) => {
        const item = toVideoItem(post);
        return item ? (
          <ShortClipCard key={post.id} video={item} creator={toCreator(post)} likes={post.likes} />
        ) : null;
      })}
    </ShortsGrid>
  );
}

function RailRows({ posts, empty }: { posts: Post[]; empty: string }) {
  if (posts.length === 0) return <RailNote>{empty}</RailNote>;
  const { items, creators } = toCards(posts);
  return (
    <RailList>
      {items.map((v) => (
        <li key={v.id}>
          <RailVideoItem video={v} creator={creators[v.authorId]} />
        </li>
      ))}
    </RailList>
  );
}

function ChipResults({ chip }: { chip: RealChipId }) {
  const filter = chipFilter(chip);
  const { posts, state, done, loadMore } = useVideoList(filter);
  if (chip === 'live') {
    return (
      <Note role="status">
        Live streaming is coming soon to TruXSocial. Live channels will appear here.
      </Note>
    );
  }
  if (state === 'loading' || state === 'idle') return <Note role="status">Loading videos…</Note>;
  if (state === 'error') return <Note role="alert">Couldn&apos;t load videos. Try again.</Note>;
  if (posts.length === 0) {
    return (
      <Note>
        {chip === 'following' ? 'No videos from people you follow yet.' : 'No videos here yet.'}
      </Note>
    );
  }
  return (
    <>
      {chip === 'flash' ? <ShortsRow posts={posts} /> : <VideoGrid posts={posts} />}
      {!done && (
        <More>
          <Button type="button" variant="secondary" onClick={() => void loadMore()}>
            Show more
          </Button>
        </More>
      )}
    </>
  );
}

export interface RealVideosPageProps {
  home: VideosHome;
}

export function RealVideosPage({ home: loaded }: RealVideosPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Hidden ("Not interested") or deleted from the Featured ··· menu, or a creator just muted:
  // gone from the page at once. These feeds aren't in the post store, which is what does
  // this for the home feed; the server leaves them out on the next load.
  const [gone, setGone] = useState<ReadonlySet<string>>(() => new Set());
  const isMuted = useIsMuted();
  const home = useMemo<VideosHome>(() => {
    const keep = (p: Post) => !gone.has(p.id) && !isMuted(p.handle);
    return {
      ...loaded,
      featured: loaded.featured && keep(loaded.featured.post) ? loaded.featured : null,
      trending: loaded.trending.filter(keep),
      latest: loaded.latest.filter(keep),
      shorts: loaded.shorts.filter(keep),
    };
  }, [loaded, gone, isMuted]);
  // ?chip=clips is the name before the Flash rename — keep old links working.
  const raw = searchParams.get('chip') === 'clips' ? 'flash' : searchParams.get('chip');
  const chip: RealChipId = raw && CHIP_IDS.has(raw) ? (raw as RealChipId) : 'all';
  const { isAuthenticated } = useSession();
  const watchLater = useWatchLater(Boolean(isAuthenticated));

  const featured = home.featured;
  const featuredItem = featured ? toVideoItem(featured.post) : null;
  const trendingOrLatest = home.trending.length > 0 ? home.trending : home.latest;
  const upNext = useMemo(
    () => home.latest.filter((p) => p.id !== featured?.post.id).slice(0, 3),
    [home.latest, featured],
  );

  const rightRail = useMemo(
    () => (
      <RailGroup pageKey="videos">
        <RailPanel id="up-next" label="Up Next">
          <Panel>
            <SectionLabel as="h2">Up Next</SectionLabel>
            <RailRows posts={upNext} empty="More videos will show up here." />
          </Panel>
        </RailPanel>
        <RailPanel id="live" label="Live Channels">
          <Panel>
            <SectionLabel as="h2">Live Channels</SectionLabel>
            <RailNote>Live streaming is coming soon.</RailNote>
          </Panel>
        </RailPanel>
        <RailPanel id="watch-later" label="Watch Later">
          <Panel>
            <SectionLabel as="h2">Watch Later</SectionLabel>
            {isAuthenticated ? (
              watchLater.posts === null ? (
                <RailNote>Loading…</RailNote>
              ) : (
                <RailRows
                  posts={watchLater.posts.slice(0, 3)}
                  empty="Tap Save on any video to watch it later."
                />
              )
            ) : (
              <RailNote>Sign in to save videos for later.</RailNote>
            )}
          </Panel>
        </RailPanel>
      </RailGroup>
    ),
    [upNext, isAuthenticated, watchLater.posts],
  );
  useRightRail(rightRail);

  return (
    <section data-testid="real-videos-page">
      <NetworkLinks destination="videos" />
      <Header>
        <Title>Video</Title>
        <Subtitle>Watch. Share. Be part of what&apos;s next.</Subtitle>
      </Header>

      <FilterChips
        chips={REAL_CHIPS}
        active={chip}
        onChange={(id) => setSearchParams(id === 'all' ? {} : { chip: id })}
        ariaLabel="Video filters"
      />

      {chip !== 'all' ? (
        <ChipResults chip={chip} />
      ) : (
        <>
          {featured && featuredItem && (
            <FeaturedVideoHero
              video={featuredItem}
              creator={toCreator(featured.post)}
              reason={FEATURED_REASON[featured.reason]}
              likes={featured.post.likes}
              comments={featured.post.comments}
              menu={
                <FeaturedPostMenu
                  post={featured.post}
                  onGone={() => setGone((ids) => new Set(ids).add(featured.post.id))}
                  onBack={() =>
                    setGone((ids) => {
                      const next = new Set(ids);
                      next.delete(featured.post.id);
                      return next;
                    })
                  }
                />
              }
            />
          )}

          {trendingOrLatest.length > 0 && (
            <>
              <SectionHead>
                <SectionHeading>
                  {home.trending.length > 0 ? 'Trending Now' : 'Latest videos'}
                </SectionHeading>
                {home.trending.length > 0 && (
                  <ViewAll to="/videos?chip=trending">View all →</ViewAll>
                )}
              </SectionHead>
              <VideoGrid posts={trendingOrLatest.slice(0, 4)} />
            </>
          )}

          {home.shorts.length > 0 && (
            <>
              <SectionHead>
                <SectionHeading>Flash</SectionHeading>
                <ViewAll to="/videos?chip=flash">View all →</ViewAll>
              </SectionHead>
              <ShortsRow posts={home.shorts.slice(0, 6)} />
            </>
          )}
        </>
      )}
    </section>
  );
}
